import { useEffect, useMemo, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import InputLabel from '@material-ui/core/InputLabel';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import useAsync from 'react-use/lib/useAsync';

import { migrationApiRef } from '../../migration/api';
import { filterServicesByType } from '../../migration/serviceTypes';
import {
  MigrationStatus,
  statusFilterLabel,
  statusLabel,
} from '../../migration/common';
// Shared with MigrationServicePicker rather than copied: both fields have to
// read the same team field and agree on what "the team" is.
import {
  clusterSummary,
  cleanArray,
  teamNameFromRef,
} from '../migrationServicePicker/MigrationServicePicker';

/** One selected service plus its cut-over answer. */
type ServiceChoice = { service: string; verificationEligible: boolean };

/**
 * Team-scoped multi-select of services that emits an ARRAY OF OBJECTS: one row
 * per selected service, each carrying a per-service checkbox.
 *
 *   [{ service: 'api', verificationEligible: true }, ...]
 *
 * `verificationEligible` is not a sign-off box — it changes what the migration
 * does at cut-over, which is why it is asked per service rather than once for
 * the whole run.
 */
const MigrationServiceTableFieldSchema = makeFieldSchema({
  output: z =>
    z.array(
      z.object({
        service: z.string(),
        verificationEligible: z.boolean(),
      }),
    ),
  uiOptions: z =>
    z.object({
      serviceTypeFilter: z.enum(['lambda', 'non-lambda']).optional(),
      dependsOn: z
        .string()
        .optional()
        .describe(
          'The field name in the form data holding the team, defaults to "team". Its value may be an entity ref (`group:nte/nte`) or a bare team name.',
        ),
      statusFilter: z
        .string()
        .optional()
        .describe(
          'Only list services at one migration status or a comma-separated list of exact statuses, e.g. "step1-completed,step2-failed". Empty means no filter.',
        ),
    }),
});

export const MigrationServiceTableSchema = MigrationServiceTableFieldSchema;

type Props = typeof MigrationServiceTableFieldSchema.TProps;

const VERIFIABLE_LABEL = 'Verifiable at production capacity';

const VERIFIABLE_EXPLANATION =
  `Leave "${VERIFIABLE_LABEL}" checked when the service can be verified on the ` +
  'new Deployment at the capacity it runs at in production. Unchecking it is not ' +
  'a sign-off box — it changes the cut-over itself: the migration sizes the ' +
  'passive side differently, resolves the active blue/green colour from the ' +
  'running pods rather than from the switcher, and takes extra backups at ' +
  'Step 2. Uncheck it only for a service you cannot exercise at full load.';

export const MigrationServiceTable = ({
  onChange,
  rawErrors,
  required,
  formData,
  formContext,
  uiSchema,
  idSchema,
  schema,
}: Props) => {
  const migrationApi = useApi(migrationApiRef);

  const options = uiSchema?.['ui:options'] ?? {};
  const dependsOnField = options.dependsOn ?? 'team';
  const statusFilter = (options.statusFilter ?? '').trim();
  const serviceTypeFilter = options.serviceTypeFilter;

  const team = teamNameFromRef(
    (formContext?.formData as Record<string, unknown> | undefined)?.[
      dependsOnField
    ],
  );

  const { value, loading, error } = useAsync(async () => {
    if (!team) return undefined;
    return await migrationApi.listServices(
      team,
      statusFilter ? statusFilter : undefined,
      serviceTypeFilter,
    );
  }, [migrationApi, team, statusFilter, serviceTypeFilter]);

  const services = useMemo(
    () => filterServicesByType(value?.services ?? [], serviceTypeFilter),
    [value, serviceTypeFilter],
  );

  const rows = useMemo(() => cleanArray<ServiceChoice>(formData), [formData]);
  // RJSF seeds the array to `minItems` with a placeholder before the operator
  // picks anything. Hiding it is not enough -- an untouched form would submit
  // that placeholder to the workflow -- so the cleaned value is written back
  // once. Guarded on length, so this cannot loop.
  useEffect(() => {
    if (Array.isArray(formData) && formData.length !== rows.length) {
      onChange(rows);
    }
  }, [formData, rows, onChange]);

  const selectedNames = useMemo(() => rows.map(r => r.service), [rows]);

  // Last checkbox answer per service, kept even while a service is deselected
  // so that removing one service never rewrites the answers of the others, and
  // re-adding a service brings its answer back instead of silently resetting
  // it to the default. Reset when the team changes: service names are only
  // unique within a team.
  const [remembered, setRemembered] = useState<Record<string, boolean>>({});
  useEffect(() => setRemembered({}), [team]);

  const rememberRows = (previous: Record<string, boolean>) => {
    const next = { ...previous };
    for (const row of rows) next[row.service] = row.verificationEligible;
    return next;
  };

  const handleSelectionChange = (names: string[]) => {
    const current = new Map(rows.map(r => [r.service, r.verificationEligible]));
    setRemembered(rememberRows);
    onChange(
      names.map(service => ({
        service,
        // Kept for services still selected, restored for ones coming back, and
        // the recorded DB choice for new selections, otherwise the form default.
        verificationEligible:
          current.get(service) ??
          remembered[service] ??
          services.find(row => row.name === service)?.verificationEligible ??
          true,
      })),
    );
  };

  // Header checkbox state. `!== false` rather than truthiness: the answer
  // defaults to true, so a row that has never been touched counts as verifiable.
  const allVerifiable =
    rows.length > 0 && rows.every(r => r.verificationEligible !== false);
  const someVerifiable = rows.some(r => r.verificationEligible !== false);

  /**
   * Set the verification answer on every selected row at once.
   *
   * Goes through the same remembered-state update a single row does, so a
   * service deselected and re-added later still returns with the answer the
   * operator last gave it rather than silently reverting to the default.
   */
  const handleToggleAll = (checked: boolean) => {
    setRemembered(previous => {
      const next = { ...previous };
      for (const row of rows) next[row.service] = checked;
      return next;
    });
    onChange(rows.map(row => ({ ...row, verificationEligible: checked })));
  };

  const handleToggle = (service: string, checked: boolean) => {
    setRemembered(previous => ({ ...previous, [service]: checked }));
    onChange(
      rows.map(row =>
        row.service === service
          ? { ...row, verificationEligible: checked }
          : row,
      ),
    );
  };

  // Drop rows the current team does not own — this is what clears the table
  // when the team changes. Skipped while loading and on error so a failed
  // request does not wipe the operator's answers.
  useEffect(() => {
    if (loading || rows.length === 0) return;
    if (!team) {
      onChange([]);
      return;
    }
    if (error || !value) return;
    const available = new Set(services.map(s => s.name));
    const kept = rows.filter(row => available.has(row.service));
    if (kept.length !== rows.length) {
      onChange(kept);
    }
  }, [loading, error, value, team, services, rows, onChange]);

  const helperText = () => {
    if (!team) return `Select a team in "${dependsOnField}" first.`;
    if (loading) return `Loading ${team}'s services…`;
    // The backend distinguishes "you are not a member of this team" from a
    // lookup failure; show its wording rather than an empty dropdown.
    if (error) return `Could not load services: ${error.message}`;
    if (services.length === 0) {
      return statusFilter
        ? `No services of '${team}' match the allowed migration statuses: ${statusFilterLabel(statusFilter)}.`
        : `No services found for '${team}' in the migration database.`;
    }
    return `${rows.length} of ${services.length} service(s) selected`;
  };

  const byName = useMemo(
    () => new Map(services.map(s => [s.name, s])),
    [services],
  );

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && rows.length === 0}
      fullWidth
    >
      <InputLabel htmlFor={idSchema?.$id}>
        {schema.title ?? 'Services'}
      </InputLabel>
      <Select
        multiple
        id={idSchema?.$id}
        value={selectedNames}
        disabled={!team || loading || Boolean(error)}
        onChange={e => handleSelectionChange(e.target.value as string[])}
        renderValue={v => (
          <>
            {(v as string[]).map(name => (
              <Chip key={name} label={name} size="small" />
            ))}
          </>
        )}
      >
        {services.map(service => (
          <MenuItem key={service.name} value={service.name}>
            <Checkbox checked={selectedNames.includes(service.name)} />
            <ListItemText
              primary={service.name}
              secondary={`${clusterSummary(service)} · ${statusLabel(
                service.status,
              )}`}
            />
          </MenuItem>
        ))}
      </Select>
      <Box mt={1}>
        <Button
          size="small"
          disabled={
            !team ||
            loading ||
            Boolean(error) ||
            services.length === 0 ||
            selectedNames.length === services.length
          }
          onClick={() => handleSelectionChange(services.map(sv => sv.name))}
        >
          Select all ({services.length})
        </Button>
        <Button
          size="small"
          disabled={selectedNames.length === 0}
          onClick={() => handleSelectionChange([])}
        >
          Clear
        </Button>
      </Box>
      <FormHelperText>{helperText()}</FormHelperText>

      {rows.length > 0 && (
        <Box mt={2}>
          <Table size="small" aria-label="Selected services">
            <TableHead>
              <TableRow>
                <TableCell>Service</TableCell>
                <TableCell>Clusters</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center" style={{ width: 200, minWidth: 200 }}>
                  {VERIFIABLE_LABEL}
                  <Checkbox
                    size="small"
                    checked={allVerifiable}
                    indeterminate={someVerifiable && !allVerifiable}
                    disabled={rows.length === 0}
                    onChange={e => handleToggleAll(e.target.checked)}
                    inputProps={{
                      'aria-label': `Toggle "${VERIFIABLE_LABEL}" for every selected service`,
                    }}
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => {
                const service = byName.get(row.service);
                return (
                  <TableRow key={row.service}>
                    <TableCell>{row.service}</TableCell>
                    <TableCell>
                      {service ? clusterSummary(service) : '—'}
                    </TableCell>
                    <TableCell>
                      {service ? (
                        <MigrationStatus status={service.status} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell
                      align="center"
                      style={{ width: 100, minWidth: 100 }}
                    >
                      <Checkbox
                        checked={row.verificationEligible}
                        onChange={e =>
                          handleToggle(row.service, e.target.checked)
                        }
                        inputProps={{
                          'aria-label': `${VERIFIABLE_LABEL} — ${row.service}`,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Box mt={2}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  About &ldquo;{VERIFIABLE_LABEL}&rdquo;
                </Typography>
                <Typography variant="body2" color="textSecondary" component="p">
                  {VERIFIABLE_EXPLANATION}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {schema.description && (
        <FormHelperText id={`${idSchema?.$id}-description`}>
          {schema.description}
        </FormHelperText>
      )}
    </FormControl>
  );
};
